/**
 * Khaleej Gamers - Product Page JavaScript
 * Handles all interactive functionality for the custom product page
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initMediaGallery();
    initVariantPicker();
    initQuantitySelector();
    initAccordions();
    initZoomModal();
    initProductForm();
  }

  /**
   * Media Gallery Functionality
   */
  function initMediaGallery() {
    const gallery = document.querySelector('.kg-media-gallery');
    if (!gallery) return;

    const mainImage = gallery.querySelector('#kg-featured-image');
    const thumbnails = gallery.querySelectorAll('.kg-media-gallery__thumb');
    const prevBtn = gallery.querySelector('.kg-media-gallery__nav--prev');
    const nextBtn = gallery.querySelector('.kg-media-gallery__nav--next');
    const counter = gallery.querySelector('.kg-media-gallery__counter-current');
    
    let currentIndex = 0;
    const totalImages = thumbnails.length;

    // Thumbnail click handler
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        setActiveImage(index);
      });
    });

    // Navigation arrows
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
        setActiveImage(newIndex);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const newIndex = currentIndex === totalImages - 1 ? 0 : currentIndex + 1;
        setActiveImage(newIndex);
      });
    }

    // Keyboard navigation
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' && prevBtn) {
        prevBtn.click();
      } else if (e.key === 'ArrowRight' && nextBtn) {
        nextBtn.click();
      }
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    
    const mainWrapper = gallery.querySelector('.kg-media-gallery__main-wrapper');
    if (mainWrapper) {
      mainWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      mainWrapper.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      }, { passive: true });
    }

    function handleSwipe() {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0 && nextBtn) {
          nextBtn.click();
        } else if (diff < 0 && prevBtn) {
          prevBtn.click();
        }
      }
    }

    function setActiveImage(index) {
      if (index < 0 || index >= totalImages) return;
      
      currentIndex = index;
      const thumb = thumbnails[index];
      const newSrc = thumb.dataset.mediaSrc;
      
      // Swap image
      if (mainImage && newSrc) {
        const tempImage = new Image();
        tempImage.onload = () => {
          mainImage.src = newSrc;
        };
        tempImage.src = newSrc;
      }
      
      // Update active thumbnail
      thumbnails.forEach((t, i) => {
        t.classList.toggle('kg-media-gallery__thumb--active', i === index);
      });
      
      if (counter) counter.textContent = index + 1;

      // FIX: Scroll only the thumbnail TRACK, never the whole page
      const track = thumb.closest('.kg-media-gallery__thumbnails-track');
      if (track) {
        const thumbLeft = thumb.offsetLeft;
        const thumbWidth = thumb.offsetWidth;
        const trackWidth = track.offsetWidth;
        const scrollTarget = thumbLeft - (trackWidth / 2) + (thumbWidth / 2);
        track.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }

    // Expose function for variant image changes
    window.KGGallery = {
      setActiveImage,
      setImageBySrc: (src) => {
        const thumb = Array.from(thumbnails).find(t => t.dataset.mediaSrc.includes(src));
        if (thumb) {
          setActiveImage(parseInt(thumb.dataset.index));
        }
      }
    };
  }

  /**
   * Variant Picker Functionality
   */
  function initVariantPicker() {
    const variantButtons = document.querySelectorAll('.kg-variant-btn');
    if (!variantButtons.length) return;

    const productJson = document.querySelector('#kg-product-json');
    if (!productJson) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch (e) {
      console.error('Error parsing product JSON:', e);
      return;
    }

    const variantIdInput = document.querySelector('#kg-variant-id');
    const priceContainer = document.querySelector('#kg-product-price');
    const addToCartBtn = document.querySelector('.kg-btn--add-to-cart');
    
    // Build current options array
    let currentOptions = [];
    document.querySelectorAll('.kg-variant-group').forEach((group, index) => {
      const activeBtn = group.querySelector('.kg-variant-btn--active');
      if (activeBtn) {
        currentOptions[index] = activeBtn.dataset.value;
      }
    });

    variantButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const optionIndex = parseInt(btn.dataset.optionIndex);
        const value = btn.dataset.value;
        
        // Update current options
        currentOptions[optionIndex] = value;
        
        // Update button states
        const group = btn.closest('.kg-variant-group');
        group.querySelectorAll('.kg-variant-btn').forEach(b => {
          b.classList.remove('kg-variant-btn--active');
        });
        btn.classList.add('kg-variant-btn--active');
        
        // Update selected value display
        const selectedSpan = group.querySelector('.kg-variant-group__selected');
        if (selectedSpan) {
          selectedSpan.textContent = value;
        }
        
        // Find matching variant
        const variant = findVariant(product.variants, currentOptions);
        
        if (variant) {
          updateVariantUI(variant);
        }
      });
    });

    function findVariant(variants, options) {
      return variants.find(variant => {
        return options.every((option, index) => {
          return variant.options[index] === option;
        });
      });
    }

    function updateVariantUI(variant) {
      // Update hidden input
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
      
      // Update URL without reload
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      
      // Update price
      if (priceContainer) {
        updatePrice(variant, priceContainer);
      }
      
      // Update add to cart button
      if (addToCartBtn) {
        const textSpan = addToCartBtn.querySelector('.kg-btn__text');
        if (variant.available) {
          addToCartBtn.disabled = false;
          if (textSpan) {
            textSpan.innerHTML = `
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 6h15l-1.5 9h-12z"></path>
                <circle cx="9" cy="20" r="1"></circle>
                <circle cx="18" cy="20" r="1"></circle>
                <path d="M6 6L5 3H2"></path>
              </svg>
              Add to Cart
            `;
          }
        } else {
          addToCartBtn.disabled = true;
          if (textSpan) {
            textSpan.textContent = 'Sold Out';
          }
        }
      }
      
      // Update featured image if variant has one
      if (variant.featured_image && window.KGGallery) {
        window.KGGallery.setImageBySrc(variant.featured_image.src);
      }
      
      // Trigger custom event for other scripts
      document.dispatchEvent(new CustomEvent('kg:variant:change', {
        detail: { variant }
      }));
    }

    function updatePrice(variant, container) {
      const formatMoney = (cents) => {
        return (cents / 100).toLocaleString('en-AE', {
          style: 'currency',
          currency: 'AED'
        });
      };

      let html = '';
      
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        html = `
          <span class="kg-product__price-sale">${formatMoney(variant.price)}</span>
          <span class="kg-product__price-compare">${formatMoney(variant.compare_at_price)}</span>
          <span class="kg-product__price-badge">-${discount}%</span>
        `;
      } else {
        html = `<span class="kg-product__price-regular">${formatMoney(variant.price)}</span>`;
      }
      
      container.innerHTML = html;
    }
  }

  /**
   * Quantity Selector
   */
  function initQuantitySelector() {
    const selector = document.querySelector('.kg-quantity__selector');
    if (!selector) return;

    const input = selector.querySelector('.kg-quantity__input');
    const minusBtn = selector.querySelector('.kg-quantity__btn--minus');
    const plusBtn = selector.querySelector('.kg-quantity__btn--plus');

    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;
      if (currentValue > min) {
        input.value = currentValue - 1;
        input.dispatchEvent(new Event('change'));
      }
    });

    plusBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      const max = parseInt(input.max) || 99;
      if (currentValue < max) {
        input.value = currentValue + 1;
        input.dispatchEvent(new Event('change'));
      }
    });

    input.addEventListener('change', () => {
      let value = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;
      
      if (value < min) value = min;
      if (value > max) value = max;
      
      input.value = value;
    });
  }

  /**
   * Accordion Functionality
   */
  function initAccordions() {
    const triggers = document.querySelectorAll('.kg-accordion__trigger');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const content = trigger.nextElementSibling;
        
        trigger.setAttribute('aria-expanded', !isExpanded);
        
        if (content) {
          content.classList.toggle('kg-accordion__content--open', !isExpanded);
        }
      });
    });
  }

  /**
   * Zoom Modal
   */
  function initZoomModal() {
    const modal = document.querySelector('#kg-zoom-modal');
    if (!modal) return;

    const zoomBtn = document.querySelector('.kg-media-gallery__zoom-btn');
    const closeBtn = modal.querySelector('.kg-zoom-modal__close');
    const backdrop = modal.querySelector('.kg-zoom-modal__backdrop');
    const zoomImage = modal.querySelector('#kg-zoom-image');
    const prevBtn = modal.querySelector('.kg-zoom-modal__nav-btn--prev');
    const nextBtn = modal.querySelector('.kg-zoom-modal__nav-btn--next');
    
    const thumbnails = document.querySelectorAll('.kg-media-gallery__thumb');
    let currentZoomIndex = 0;

    function openModal(index = 0) {
      currentZoomIndex = index;
      updateZoomImage();
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeBtn?.focus();
    }

    function closeModal() {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function updateZoomImage() {
      const thumb = thumbnails[currentZoomIndex];
      if (thumb && zoomImage) {
        let src = thumb.dataset.mediaSrc;
        src = src.replace(/width=\d+/, 'width=2000');
        zoomImage.src = src;
        zoomImage.alt = thumb.querySelector('img')?.alt || '';
      }
    }

    zoomBtn?.addEventListener('click', () => {
      const activeThumb = document.querySelector('.kg-media-gallery__thumb--active');
      const index = activeThumb ? parseInt(activeThumb.dataset.index) : 0;
      openModal(index);
    });

    const mainImage = document.querySelector('.kg-media-gallery__frame');
    mainImage?.addEventListener('click', (e) => {
      if (!e.target.closest('.kg-media-gallery__zoom-btn')) {
        const activeThumb = document.querySelector('.kg-media-gallery__thumb--active');
        const index = activeThumb ? parseInt(activeThumb.dataset.index) : 0;
        openModal(index);
      }
    });

    closeBtn?.addEventListener('click', closeModal);
    backdrop?.addEventListener('click', closeModal);

    prevBtn?.addEventListener('click', () => {
      currentZoomIndex = currentZoomIndex === 0 ? thumbnails.length - 1 : currentZoomIndex - 1;
      updateZoomImage();
    });

    nextBtn?.addEventListener('click', () => {
      currentZoomIndex = currentZoomIndex === thumbnails.length - 1 ? 0 : currentZoomIndex + 1;
      updateZoomImage();
    });

    modal.addEventListener('keydown', (e) => {
      if (modal.getAttribute('aria-hidden') === 'true') return;
      
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowLeft') {
        prevBtn?.click();
      } else if (e.key === 'ArrowRight') {
        nextBtn?.click();
      }
    });
  }

  /**
   * Product Form - Add to Cart
   */
  function initProductForm() {
    const form = document.querySelector('#kg-product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const submitBtn = form.querySelector('.kg-btn--add-to-cart');
      if (!submitBtn || submitBtn.disabled) return;

      submitBtn.classList.add('kg-btn--loading');
      submitBtn.disabled = true;

      const formData = new FormData(form);
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        const result = await response.json();

        if (response.ok) {
          updateCartCount();
          showNotification('Added to cart!', 'success');
          document.dispatchEvent(new CustomEvent('kg:cart:add', {
            detail: { item: result }
          }));
        } else {
          showNotification(result.description || 'Could not add to cart', 'error');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        showNotification('Something went wrong', 'error');
      } finally {
        submitBtn.classList.remove('kg-btn--loading');
        submitBtn.disabled = false;
      }
    });
  }

  /**
   * Update cart count in header
   */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
      
      document.dispatchEvent(new CustomEvent('kg:cart:update', {
        detail: { cart }
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show notification toast
   */
  function showNotification(message, type = 'info') {
    let container = document.querySelector('.kg-notifications');
    if (!container) {
      container = document.createElement('div');
      container.className = 'kg-notifications';
      container.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 10px;
      `;
      document.body.appendChild(container);
    }

    const notification = document.createElement('div');
    notification.className = `kg-notification kg-notification--${type}`;
    notification.style.cssText = `
      padding: 1rem 1.5rem;
      border-radius: 10px;
      background: ${type === 'success' ? 'rgba(34, 197, 94, 0.9)' : type === 'error' ? 'rgba(239, 68, 68, 0.9)' : 'rgba(26, 26, 26, 0.9)'};
      color: white;
      font-weight: 500;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      transform: translateX(120%);
      transition: transform 0.3s ease;
      backdrop-filter: blur(10px);
    `;
    notification.textContent = message;
    
    container.appendChild(notification);
    
    requestAnimationFrame(() => {
      notification.style.transform = 'translateX(0)';
    });
    
    setTimeout(() => {
      notification.style.transform = 'translateX(120%)';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }

  window.KGProductPage = {
    showNotification,
    updateCartCount
  };

})();